import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => toDate(value)?.toLocaleString('hu-HU') || '—';
const formatPrice = (value) => `${(Number(value) || 0).toLocaleString('hu-HU')} Ft`;

export function AbandonedCheckoutsPanel() {
  const [checkouts, setCheckouts] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const checkoutsQuery = query(
      collection(db, 'abandonedCheckouts'),
      orderBy('updatedAt', 'desc'),
    );
    return onSnapshot(
      checkoutsQuery,
      (snapshot) => {
        setCheckouts(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
        setError('');
      },
      () => setError('Nem sikerült betölteni a félbehagyott pénztárakat.'),
    );
  }, []);

  const activeCheckouts = useMemo(() => {
    const now = Date.now();
    return checkouts.filter((checkout) => {
      const expiry = toDate(checkout.expiresAt)?.getTime();
      return checkout.status === 'active' && (!expiry || expiry > now);
    });
  }, [checkouts]);

  const handleDelete = async (checkoutId) => {
    if (!window.confirm('Biztosan törlöd ezt a félbehagyott pénztárat?')) return;
    try {
      await deleteDoc(doc(db, 'abandonedCheckouts', checkoutId));
    } catch {
      setError('A félbehagyott pénztár törlése nem sikerült.');
    }
  };

  return (
    <section className="rounded-3xl border border-border bg-card p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Aktív félbehagyott pénztárak</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Csak kifejezett hozzájárulással mentett adatok. A rendszer 14 nap után törli őket.
          </p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1 text-sm font-medium">
          {activeCheckouts.length} db
        </span>
      </div>

      {error ? (
        <p className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="space-y-4">
        {activeCheckouts.map((checkout) => (
          <details key={checkout.id} className="rounded-2xl border border-border/70 bg-background/60 p-4">
            <summary className="cursor-pointer list-none">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{checkout.customer?.name || 'Név nélkül'}</p>
                  <p className="text-sm text-muted-foreground break-all">
                    {checkout.customer?.email || '—'}
                  </p>
                </div>
                <div className="text-right text-sm">
                  <p className="font-medium">{formatPrice(checkout.total)}</p>
                  <p className="text-xs text-muted-foreground">Frissítve: {formatDate(checkout.updatedAt)}</p>
                </div>
              </div>
            </summary>

            <div className="mt-4 grid gap-5 border-t border-border/70 pt-4 md:grid-cols-2">
              <div className="text-sm">
                <p className="mb-1 text-xs text-muted-foreground">Kapcsolattartás és szállítás</p>
                <p>{checkout.customer?.phone || 'Telefonszám nincs megadva'}</p>
                <p className="break-words">
                  {[checkout.customer?.zip, checkout.customer?.city, checkout.customer?.address]
                    .filter(Boolean).join(' ') || 'Cím nincs megadva'}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Automatikus törlés: {formatDate(checkout.expiresAt)}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs text-muted-foreground">Kosár tartalma</p>
                <ul className="space-y-1 text-sm">
                  {(checkout.items || []).map((item, index) => (
                    <li key={`${checkout.id}-${index}`} className="flex justify-between gap-3">
                      <span>{item.name} × {item.quantity}</span>
                      <span>{formatPrice((Number(item.price) || 0) * (Number(item.quantity) || 0))}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => handleDelete(checkout.id)}
              >
                Azonnali törlés
              </button>
            </div>
          </details>
        ))}

        {activeCheckouts.length === 0 ? (
          <p className="rounded-2xl border border-border/70 p-6 text-sm text-muted-foreground">
            Jelenleg nincs aktív félbehagyott pénztár.
          </p>
        ) : null}
      </div>
    </section>
  );
}
